import { useEffect, useState } from "react";
import { GitCommit, Sparkles, ShieldCheck, Wrench, Bug, Rss, FileJson } from "lucide-react";
import Seo from "@/components/seo/Seo";

const API = process.env.REACT_APP_BACKEND_URL;

const TAGS = {
  feature: { icon: Sparkles, label: "Feature", c: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  security: { icon: ShieldCheck, label: "Security", c: "bg-sky-50 text-sky-700 border-sky-200" },
  improvement: { icon: Wrench, label: "Improvement", c: "bg-slate-50 text-slate-700 border-slate-200" },
  fix: { icon: Bug, label: "Fix", c: "bg-amber-50 text-amber-800 border-amber-200" },
};

function Tag({ kind }) {
  const t = TAGS[kind] || TAGS.improvement;
  const Icon = t.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-semibold rounded-full border px-2 py-0.5 ${t.c}`}>
      <Icon className="h-3 w-3" /> {t.label}
    </span>
  );
}

// Parse JSON Feed 1.1 item back into the shape the UI expects.
function parseFeedItem(item) {
  const version = item._atlasforge?.version || item.title.match(/\(([^)]+)\)$/)?.[1] || "";
  const title = item.title.replace(/\s*\([^)]+\)$/, "");
  const date = (item.date_published || "").slice(0, 10);
  // content_text is the canonical list. Each line: "- Label: text"
  const items = (item.content_text || "")
    .split("\n")
    .filter((l) => l.trim().startsWith("- "))
    .map((l) => {
      const m = l.match(/^- (\w+):\s*(.*)$/);
      if (!m) return ["improvement", l.replace(/^- /, "")];
      return [m[1].toLowerCase(), m[2]];
    });
  return { version, title, date, items };
}

export default function Changelog() {
  const [entries, setEntries] = useState([]);
  const [error, setError] = useState(null);
  const rssUrl = `${API}/api/changelog.rss`;
  const jsonUrl = `${API}/api/changelog.json`;

  useEffect(() => {
    let cancelled = false;
    fetch(jsonUrl)
      .then((r) => {
        if (!r.ok) throw new Error(`Feed returned ${r.status}`);
        return r.json();
      })
      .then((feed) => {
        if (cancelled) return;
        setEntries((feed.items || []).map(parseFeedItem));
      })
      .catch((e) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
  }, [jsonUrl]);

  return (
    <>
      <Seo
        title="Changelog — what's new at AtlasForge"
        description="Product updates, launches, and improvements across AtlasForge Financial — Safe to Spend 365, Ember360, and the APIs platform."
        path="/changelog"
      />
      <section className="relative">
        <div className="absolute inset-0 af-soft-glow pointer-events-none" />
        <div className="relative mx-auto max-w-4xl px-6 lg:px-10 pt-16 pb-10">
          <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">Platform changelog</p>
          <h1
            data-testid="changelog-title"
            className="font-display mt-3 text-[36px] md:text-[48px] leading-[1.05] text-[#0A1128]"
          >
            What&apos;s new at AtlasForge.
          </h1>
          <p className="mt-4 text-[16px] text-slate-600 max-w-2xl">
            Every meaningful change to the AtlasForge platform — Safe to
            Spend, Ember360, and the APIs for Banks &amp; Fintechs. Subscribe
            via RSS or JSON Feed, or email{" "}
            <a href="mailto:customersupport@atlasforgefinancial.com" className="underline">
              customersupport@atlasforgefinancial.com
            </a>
            .
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <a
              href={rssUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="changelog-rss-link"
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-[13px] text-slate-700 hover:border-slate-400 hover:text-[#0A1128] transition-colors"
            >
              <Rss className="h-3.5 w-3.5 text-amber-600" /> RSS feed
            </a>
            <a
              href={jsonUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="changelog-json-link"
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-[13px] text-slate-700 hover:border-slate-400 hover:text-[#0A1128] transition-colors"
            >
              <FileJson className="h-3.5 w-3.5 text-emerald-600" /> JSON Feed
            </a>
            <span className="text-[12px] text-slate-500">
              Auto-updated whenever we ship.
            </span>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-4xl px-6 lg:px-10 pb-24">
          {error && (
            <p data-testid="changelog-error" className="text-[13px] text-red-600 mb-6">
              Couldn&apos;t load the changelog feed: {error}
            </p>
          )}
          {!error && entries.length === 0 && (
            <p data-testid="changelog-loading" className="text-[13px] text-slate-500">
              Loading changelog…
            </p>
          )}
          {entries.length > 0 && (
            <ol className="relative border-l border-slate-200 pl-8 space-y-12" data-testid="changelog-list">
              {entries.map((e) => (
                <li
                  key={e.version}
                  id={e.version}
                  data-testid={`changelog-${e.version}`}
                  className="relative scroll-mt-24"
                >
                  <span className="absolute -left-[35px] top-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#0A1128] text-white">
                    <GitCommit className="h-3 w-3" />
                  </span>
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <h2 className="font-display text-[22px] text-[#0A1128]">{e.title}</h2>
                    <span className="font-mono text-[12px] text-slate-500">{e.version}</span>
                    {e.date && (
                      <span className="text-[12px] text-slate-500">
                        · {new Date(e.date).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                      </span>
                    )}
                  </div>
                  <ul className="mt-4 space-y-2.5">
                    {e.items.map(([kind, text], i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className="mt-0.5"><Tag kind={kind} /></span>
                        <span className="text-[14.5px] text-slate-700 leading-relaxed">{text}</span>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ol>
          )}
        </div>
      </section>
    </>
  );
}
